import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Form, Input, Select, Label, Button, Tooltip, Radio } from '@shared/components';
import { InfoIcon } from 'lucide-react';
import type { ConfigSetting } from '@/features/products/types/products.types';
import { AddOnCombobox } from './AddOnCombobox';
import { VatCostFields } from './VatCostFields/VatCostFields';
import { useVatDerivedCost } from '../hooks/useVatDerivedCost';
import { AssignAddOnSchema, type AssignAddOnInput } from '../schemas/productAddon.schema';
import { autoIncludeLinkOptions, isAutoIncludeLinkSelectable } from '../utils/autoIncludeLinkOptions';
import { QUOTE_BEHAVIOUR_OPTIONS, toQuoteBehaviour, fromQuoteBehaviour, type QuoteBehaviour } from '../utils/quoteBehaviour';
import type { AssignAddOnPayload } from '../services/productAddons';
import type { AddOnRecord } from '../services/smr-admin';
import type { z } from 'zod';

interface Props {
    excludeIds?: string[];
    configSettings?: ConfigSetting[] | null;
    onSubmit: (payload: AssignAddOnPayload) => void;
    isLoading: boolean;
    error?: string | null;
    onCancel: () => void;
}

// Form state carries a display-only gross field (derived by useVatDerivedCost from the
// selected add-on's category rate); the schema strips it before it reaches onSubmit.
type AssignFormInput = z.input<typeof AssignAddOnSchema>;

const AUTO_INCLUDE_REGION_ID = 'auto-include-region';

const PERIOD_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
];

const HELP = {
    net: 'Optional net price for this optional extra on this product only. Blank uses the default net price.',
    freqLimit: 'Maximum number of times the optional extra can be claimed within the chosen period.',
    freqPeriod: 'The period the frequency limit applies over. Set together with the limit.',
    qty: 'Optional cap on how many of this optional extra can be added to the product.',
    quoteBehaviour: 'Optional: the seller adds it. Auto-included: seeded on every quote, removable. Mandatory: seeded and locked.',
    link: 'Which service (or every service) seeds a line for this optional extra.',
};

// A field label with a hover tooltip explaining the field.
function FieldLabel({ htmlFor, label, help }: { htmlFor: string; label: string; help: string }) {
    return (
        <Label htmlFor={htmlFor}>
            <span className="inline-flex items-center gap-1">
                {label}
                <Tooltip content={help}>
                    <InfoIcon size={14} className="text-mp-muted" />
                </Tooltip>
            </span>
        </Label>
    );
}

export function AddOnAssignmentForm({ excludeIds, configSettings, onSubmit, isLoading, error, onCancel }: Props) {
    const [selectedAddon, setSelectedAddon] = useState<AddOnRecord | null>(null);

    const { handleSubmit, control, watch, setValue } = useForm<AssignFormInput, unknown, AssignAddOnInput>({
        resolver: zodResolver(AssignAddOnSchema),
        defaultValues: {
            addon_id: '',
            price_override: '',
            gross_override_display: null,
            frequency_limit: '',
            frequency_period: '',
            quantity_limit: '',
            auto_include: false,
            mandatory: false,
            auto_include_link: null,
        },
    });

    useVatDerivedCost({
        watch,
        setValue,
        netField: 'price_override',
        grossField: 'gross_override_display',
        fixedRatePercent: selectedAddon?.vat_category?.rate_percent ?? '0',
    });

    const quoteBehaviour = toQuoteBehaviour({ auto_include: watch('auto_include'), mandatory: watch('mandatory') });
    const autoIncludeLink = watch('auto_include_link') as string | null;
    const linkOptions = useMemo(() => autoIncludeLinkOptions(configSettings), [configSettings]);

    useEffect(() => {
        if (!isAutoIncludeLinkSelectable(autoIncludeLink, linkOptions)) {
            setValue('auto_include_link', null);
        }
    }, [linkOptions, autoIncludeLink, setValue]);

    const handleQuoteBehaviourChange = (value: QuoteBehaviour) => {
        const { auto_include, mandatory } = fromQuoteBehaviour(value);
        setValue('auto_include', auto_include);
        setValue('mandatory', mandatory);
        if (value === 'optional') {
            setValue('auto_include_link', null);
        }
    };

    const handleSubmitForm = (data: AssignAddOnInput) => {
        onSubmit(data);
    };

    return (
        <Form className="space-y-4 max-w-2xl" onSubmit={handleSubmit(handleSubmitForm)}>
            {error && <Alert variant="error">{error}</Alert>}

            <div>
                <Label htmlFor="addon">Optional Extra</Label>
                <Controller
                    name="addon_id"
                    control={control}
                    render={({ field, fieldState: { error: fieldError } }) => (
                        <>
                            <AddOnCombobox
                                id="addon"
                                value={selectedAddon}
                                onChange={(addon) => {
                                    setSelectedAddon(addon);
                                    field.onChange(addon?.id ?? '');
                                }}
                                excludeIds={excludeIds}
                            />
                            {fieldError && (
                                <p className="text-mp-error text-xs mt-1">{fieldError.message}</p>
                            )}
                        </>
                    )}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <VatCostFields<AssignFormInput>
                    control={control}
                    hideCategory
                    netField="price_override"
                    grossField="gross_override_display"
                    categories={[]}
                    disabled={isLoading}
                    labels={{ net: 'Net Price Override (£)', gross: 'Gross Override (£)' }}
                    netRequired={false}
                />

                <div>
                    <FieldLabel htmlFor="frequency_limit" label="Frequency Limit" help={HELP.freqLimit} />
                    <Controller
                        name="frequency_limit"
                        control={control}
                        render={({ field, fieldState: { error: fieldError } }) => (
                            <Input
                                id="frequency_limit"
                                type="number"
                                min="1"
                                placeholder="e.g. 5"
                                value={field.value == null ? '' : String(field.value)}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!fieldError}
                                hint={fieldError?.message}
                            />
                        )}
                    />
                </div>

                <div>
                    <FieldLabel htmlFor="frequency_period" label="Frequency Period" help={HELP.freqPeriod} />
                    <Controller
                        name="frequency_period"
                        control={control}
                        render={({ field, fieldState: { error: fieldError } }) => (
                            <Select
                                id="frequency_period"
                                options={PERIOD_OPTIONS}
                                placeholder="None"
                                value={field.value == null ? '' : String(field.value)}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!fieldError}
                                hint={fieldError?.message}
                            />
                        )}
                    />
                </div>

                <div>
                    <FieldLabel htmlFor="quantity_limit" label="Quantity Limit" help={HELP.qty} />
                    <Controller
                        name="quantity_limit"
                        control={control}
                        render={({ field, fieldState: { error: fieldError } }) => (
                            <Input
                                id="quantity_limit"
                                type="number"
                                min="1"
                                placeholder="e.g. 2"
                                value={field.value == null ? '' : String(field.value)}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!fieldError}
                                hint={fieldError?.message}
                            />
                        )}
                    />
                </div>
            </div>

            <div className="space-y-4">
                <fieldset
                    className="rounded-lg border border-mp-border p-4"
                    aria-controls={quoteBehaviour !== 'optional' ? AUTO_INCLUDE_REGION_ID : undefined}
                >
                    <legend className="px-2">
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-mp-foreground">
                            Quote behaviour
                            <Tooltip content={HELP.quoteBehaviour}>
                                <InfoIcon size={14} className="text-mp-muted" />
                            </Tooltip>
                        </span>
                    </legend>
                    <div className="space-y-3">
                        {QUOTE_BEHAVIOUR_OPTIONS.map((option) => (
                            <div key={option.value}>
                                <Radio
                                    id={`quote_behaviour_${option.value}`}
                                    name="quote_behaviour"
                                    value={option.value}
                                    checked={quoteBehaviour === option.value}
                                    label={option.label}
                                    disabled={isLoading}
                                    onChange={(value) => handleQuoteBehaviourChange(value as QuoteBehaviour)}
                                />
                                <p className="ml-8 text-xs text-mp-muted">{option.help}</p>
                            </div>
                        ))}
                    </div>
                </fieldset>

                {quoteBehaviour !== 'optional' && (
                    <div id={AUTO_INCLUDE_REGION_ID}>
                        <FieldLabel htmlFor="auto_include_link" label="Add with" help={HELP.link} />
                        <Controller
                            name="auto_include_link"
                            control={control}
                            render={({ field, fieldState: { error: fieldError } }) => (
                                <Select
                                    id="auto_include_link"
                                    ariaLabel="Add with"
                                    disabled={isLoading}
                                    options={linkOptions}
                                    placeholder="Select link"
                                    value={field.value == null ? '' : String(field.value)}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    error={!!fieldError}
                                    hint={fieldError?.message}
                                />
                            )}
                        />
                    </div>
                )}
            </div>

            <div className="flex items-center gap-2">
                <Button type="submit" variant="primary" disabled={isLoading}>
                    {isLoading ? 'Assigning…' : 'Assign Optional Extra'}
                </Button>
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                    Cancel
                </Button>
            </div>
        </Form>
    );
}
